import { StatusPill } from "nazare-ui/components";
import { Tooltip, TooltipContent, TooltipTrigger } from "nazare-ui/primitives";
import type { WorkflowRunPauseState } from "../../../../workflow-run-control";
import { workflowRunStatusPresentation } from "../../../../workflow-run-status";

/**
 * Status pill for a workflow run or step — ported from
 * nazare-frontend's `screens/admin/workflows/status-chip.tsx`, rebuilt on
 * `nazare-ui`'s shared `StatusPill` (this app's chip primitive) instead of a
 * bespoke Tailwind-classed span, with a pulsing dot layered in for
 * running/paused so it still reads as live. `status` is an open string on
 * the wire (the BFF does not close the enum), so unknown values fall back
 * to a neutral pill rather than throwing.
 */
export function StatusChip({ status, pauseState = null, pauseRequested = false }: { status: string; pauseState?: WorkflowRunPauseState; pauseRequested?: boolean }) {
  const { label, tone, pulse, tooltip } = workflowRunStatusPresentation(status, pauseState, pauseRequested);
  const pill = (
    <StatusPill tone={tone} className="inline-flex items-center gap-1.5">
      {pulse && <span className="w-1.5 h-1.5 bg-current rounded-full animate-pulse" />}
      {label}
    </StatusPill>
  );
  if (!tooltip) return pill;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="inline-flex rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--blue)]"
        >
          {pill}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * How a run was triggered — a muted pill distinguishing a manual dispatch
 * from an automatic scheduled run. Renders nothing when the trigger is
 * unknown.
 */
export function TriggerChip({ trigger }: { trigger: string | null }) {
  if (!trigger) return null;
  const label = trigger === "scheduled" ? "Scheduled" : trigger === "manual" ? "Manual" : trigger;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-[var(--card-2)] text-[var(--nzr-muted)] border border-[var(--nzr-border)]">
      {label}
    </span>
  );
}

/**
 * Pill marking an "immortal" workflow — one long-lived run that loops
 * forever rather than completing. Sits next to the trigger pill in the run
 * header.
 */
export function ImmortalChip({ immortal }: { immortal?: boolean }) {
  if (!immortal) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-[var(--blue-bg)] text-[var(--blue)] border border-[var(--blue)]">
      Immortal
    </span>
  );
}
